'use client';

import BannerSection from '@/components/common/bottom-banner/bottom-banner-section';
import CommonButton from '@/components/common/common-button/common-button';
import { ChargeawayBg, ChargeawayDarkBg, ChargeawayFeatureCover, ChargeawayHeroCover, DevStickyNote1, DevStickyNote2, DevStickyNote3, DevStickyNote4, LaunchStickyNote1, LaunchStickyNote2, LaunchStickyNote3, LaunchStickyNote4, LogoPrecisionEngineeringDevkrest, StickyNote, StickyNote1, StickyNote2, StickyNote3, TestStickyNote1, TestStickyNote2, TestStickyNote3, TestStickyNote4 } from '@/lib/images';
import Image from 'next/image';
import Link from 'next/link';

// Modern Cube Component based on Figma design with true iOS-style smooth corners
const ModernCube = ({
    color = "#0061F1",
    size = 133,
    className = ""
}: {
    color?: string;
    size?: number;
    className?: string;
}) => {
    // Convert hex color to RGB for shadow
    const hexToRgb = (hex: string) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : { r: 0, g: 97, b: 241 }; // fallback to default blue
    };

    const rgb = hexToRgb(color);
    const shadowColor = `${rgb.r}, ${rgb.g}, ${rgb.b}`;
    return (
        <div
            className='overflow-hidden'
            style={{
                padding: '1rem 1.25rem',
                backgroundColor: color,
                color: '#fff',
                borderRadius: `${Math.round(size * 0.3)}px`,
                boxShadow: `
                0px 2px ${Math.round(size * 0.243)}px ${Math.round(size * 0.083)}px rgba(${shadowColor}, 0.33),
                0px 2px ${Math.round(size * 0.110)}px ${Math.round(size * 0.120)}px rgba(255, 255, 255, 0.05) inset,
                0px 2px ${Math.round(size * 0.371)}px ${Math.round(size * 0.248)}px rgba(255, 255, 255, 0.08) inset
            `,
                height: `${size}px`,
                width: `${size}px`,
                display: 'inline-block',
            }}
        >{""}
        </div>
    );
};

export default function ChargeawayAppCaseStudy() {
    return (
        <div className="min-h-screen">
            {/* Hero Section */}
            <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
                {/* Background Elements */}
                <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-black"></div>

                {/* Hero Background Image */}
                <div className="absolute inset-0">
                    <Image
                        src="/images/case-studies/ev-charging-solution/chargeaway-bg-hero.webp"
                        alt="ChargeAway Hero Background"
                        fill
                        className="object-cover"
                        priority
                    />
                    <div className="absolute inset-0 bg-black/40"></div>
                </div>

                {/* Main Content */}
                <div className="mt-20 md:mt-0 relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 lg:gap-12 items-center w-full py-20 lg:py-0">
                        {/* Left Section - Text Content */}
                        <div className="space-y-6 lg:space-y-8 pt-10 lg:pt-20">
                            {/* Logo and Title */}
                            <div className="space-y-4 lg:space-y-6">
                                {/* Logo */}
                                <div className="flex items-center gap-3 lg:gap-4">
                                    <Image
                                        src="/images/case-studies/ev-charging-solution/app-logo.svg"
                                        alt="ChargeAway Logo"
                                        width={48}
                                        height={48}
                                        className="w-12 h-12 lg:w-16 lg:h-16 object-contain"
                                    />
                                    <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white">
                                        ChargeAway
                                    </h1>
                                </div>

                                <div className="text-xl sm:text-2xl lg:text-3xl font-semibold text-white/80">
                                    Mobile App Case Study
                                </div>
                            </div>

                            {/* Description */}
                            <div className="space-y-4">
                                <p className="text-base sm:text-lg text-white/90 leading-relaxed">
                                    The electric car market has witnessed a booming growth in recent years, thus creating the need for a dedicated and optimal EV charging infrastructure at various levels of the distribution grid in order to ensure a healthy ecosystem which remains pivotal for the sustainable future of our planet.
                                </p>
                            </div>
                        </div>

                        {/* Right Section - App Mockup */}
                        <div className="relative flex justify-center lg:justify-end h-full items-end">
                            <div className="relative transform translate-y-26 scale-120 md:scale-110 lg:translate-y-28">
                                <Image
                                    src={ChargeawayHeroCover}
                                    alt="ChargeAway App Hero Mockup"
                                    className="w-full h-auto max-w-xs sm:max-w-sm lg:max-w-md xl:max-w-lg"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Project Overview Section */}
            <div className="bg-white relative overflow-hidden border-b border-gray-300">
                <div className="max-w-auto mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[400px] lg:min-h-[600px]">
                        {/* Left Side */}
                        <div className="lg:col-span-1 px-6 sm:px-12 lg:px-20 flex items-end lg:border-r lg:border-gray-300 lg:pr-8 py-12 lg:py-0">
                            <div className="space-y-4 lg:space-y-6">
                                {/* App Icon */}
                                <Image
                                    src="/images/case-studies/ev-charging-solution/light_bolt.webp"
                                    alt="Lightning Bolt"
                                    width={60}
                                    height={60}
                                    className="w-12 h-12 lg:w-16 lg:h-16"
                                />

                                {/* Project Overview */}
                                <div className="space-y-4 pb-6 lg:pb-10">
                                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">
                                        <span className="text-gray-200 font-normal">01.{" "}</span>About project (MVP)
                                    </h2>
                                    <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                                        The goal of the ChargeAway{" "}
                                        <Link href="/mobile-app-development-services" className="text-[#0061F1] underline underline-offset-2 hover:opacity-80">
                                            mobile application
                                        </Link>{" "}
                                        is to help EV drivers find and access charging stations efficiently, reduce range anxiety, and contribute to a sustainable future by promoting electric vehicle adoption through seamless charging infrastructure.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Right Side - Three Sections Layout */}
                        <div className="lg:col-span-1 flex flex-col h-full">
                            {/* Top Row - Problem Statement (Full Width) */}
                            <div className="flex flex-col lg:flex-row border-b border-gray-300 py-8 lg:py-12">
                                {/* Problem Statement */}
                                <div className="w-full px-6 sm:px-12 lg:px-20">
                                    <div className="text-gray-900">
                                        <h3 className="text-xl sm:text-2xl font-bold mb-4">Problem Statement</h3>
                                        <p className="text-sm sm:text-base text-gray-700 mb-4">EV drivers face significant challenges finding reliable charging stations, leading to range anxiety and limited adoption of electric vehicles.</p>
                                        <p className="text-sm sm:text-base text-gray-700 mb-4">Existing solutions lack real-time availability updates, user-friendly interfaces, and comprehensive station information, creating frustration and uncertainty for drivers.</p>
                                        <p className="text-sm sm:text-base text-gray-700 mb-4">The market needs a seamless, intuitive platform that eliminates charging anxiety and promotes sustainable transportation adoption.</p>
                                        <p className="text-sm sm:text-base text-gray-700">Additionally, there's a lack of integrated payment systems, route optimization for charging stops, and community-driven station reviews that could enhance the overall charging experience.</p>
                                    </div>
                                </div>
                            </div>

                            {/* Section 3 - Solution */}
                            <div className="flex items-start px-6 sm:px-12 lg:px-20 py-8 lg:py-12">
                                <div className="text-gray-900">
                                    <h3 className="text-xl sm:text-2xl font-bold mb-4">Solution</h3>
                                    <p className="text-sm sm:text-base text-gray-700 mb-4">ChargeAway is a comprehensive mobile application that revolutionizes the EV charging experience by providing real-time station availability, integrated payment systems, and intelligent route planning.</p>
                                    <p className="text-sm sm:text-base text-gray-700 mb-4">The app features an intuitive interface with interactive maps, detailed station information, user reviews, and seamless payment processing to eliminate range anxiety and promote EV adoption.</p>
                                    <p className="text-sm sm:text-base text-gray-700 mb-4">Key features include real-time availability updates, route optimization for charging stops, community-driven reviews, integrated payment solutions, and the ability for users to host their home chargers for additional income.</p>
                                    <p className="text-sm sm:text-base text-gray-700 mb-4">The platform connects drivers with reliable charging infrastructure while providing essential tools for planning, navigation, and payment management, creating a community-driven network of charging options.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Background Image Section */}
            <section className="relative">
                <Image
                    src={ChargeawayBg}
                    alt="ChargeAway Background"
                    className="w-full h-auto"
                />
            </section>

            {/* Research Outcomes Section */}
            <section className="bg-white py-12 sm:py-16 lg:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                            <span className="text-gray-200 font-normal">02.{" "}</span>Research Outcomes
                        </h2>
                        <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
                            Developed by mapping customer pain points to solutions and defining success through measurable outcomes.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mb-12">
                        {/* Customer */}
                        <div className="p-8 border border-gray-200">
                            <div className="flex items-center mb-6">
                                <div className="bg-green-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                                    Customer
                                </div>
                            </div>
                            <ul className="space-y-3">
                                <li className="flex items-start">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                    <span className="text-gray-700">EV drivers & owners</span>
                                </li>
                                <li className="flex items-start">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                    <span className="text-gray-700">Home charger hosts</span>
                                </li>
                                <li className="flex items-start">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                    <span className="text-gray-700">Commercial charging stations</span>
                                </li>
                            </ul>
                        </div>

                        {/* Obstacles */}
                        <div className="p-8 border border-gray-200">
                            <div className="flex items-center mb-6">
                                <div className="bg-green-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                                    Obstacles
                                </div>
                            </div>
                            <ul className="space-y-3">
                                <li className="flex items-start">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                    <span className="text-gray-700">Range anxiety</span>
                                </li>
                                <li className="flex items-start">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                    <span className="text-gray-700">Limited charging infrastructure</span>
                                </li>
                                <li className="flex items-start">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                    <span className="text-gray-700">Payment complexity</span>
                                </li>
                                <li className="flex items-start">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                    <span className="text-gray-700">Unreliable station availability</span>
                                </li>
                            </ul>
                        </div>

                        {/* Solution */}
                        <div className="p-8 border border-gray-200">
                            <div className="flex items-center mb-6">
                                <div className="bg-green-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                                    Solution
                                </div>
                            </div>
                            <ul className="space-y-3">
                                <li className="flex items-start">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                    <span className="text-gray-700">Real-time availability tracking</span>
                                </li>
                                <li className="flex items-start">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                    <span className="text-gray-700">Integrated payment system</span>
                                </li>
                                <li className="flex items-start">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                    <span className="text-gray-700">Home charger hosting network</span>
                                </li>
                                <li className="flex items-start">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                    <span className="text-gray-700">Intelligent route planning</span>
                                </li>
                                <li className="flex items-start">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                    <span className="text-gray-700">Community reviews & ratings</span>
                                </li>
                            </ul>
                        </div>

                        {/* Success Metrics */}
                        <div className="p-8 border border-gray-200">
                            <div className="flex items-center mb-6">
                                <div className="bg-green-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                                    Projected Success
                                </div>
                            </div>
                            <ul className="space-y-3">
                                <li className="flex items-start">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                    <span className="text-gray-700">25,000+ downloads in first 6 months</span>
                                </li>
                                <li className="flex items-start">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                    <span className="text-gray-700">1,000+ charging stations mapped</span>
                                </li>
                                <li className="flex items-start">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                    <span className="text-gray-700">4.2+ star app store rating</span>
                                </li>
                                <li className="flex items-start">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                    <span className="text-gray-700">500+ home charger listings</span>
                                </li>
                                <li className="flex items-start">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                    <span className="text-gray-700">$50K+ revenue in first year</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* Objective */}
                    <div className="p-8 border border-gray-200">
                        <div className="flex items-center mb-6">
                            <div className="bg-green-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                                Objective
                            </div>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div>
                                <h4 className="font-semibold text-gray-900 mb-3">For EV Drivers:</h4>
                                <p className="text-gray-700">Provide seamless access to reliable charging infrastructure, eliminate range anxiety, and create a community-driven network that makes EV ownership more accessible and convenient.</p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-900 mb-3">For Home Hosts:</h4>
                                <p className="text-gray-700">Enable users to monetize their home charging infrastructure while contributing to the EV ecosystem, creating additional income streams and promoting sustainable transportation.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* App Features Section */}
            <section className="relative py-12 sm:py-16 lg:py-20 overflow-hidden">
                {/* Background Image */}
                <div className="absolute inset-0">
                    <Image
                        src={ChargeawayDarkBg}
                        alt="ChargeAway Dark Background"
                        fill
                        className="object-cover"
                    />
                    <div className="absolute inset-0 bg-black/20"></div>
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
                            <span className="text-gray-600 font-normal">03.{" "}</span>Key Features of ChargeAway
                        </h2>
                        <p className="text-base sm:text-lg lg:text-xl text-gray-300 max-w-3xl mx-auto">
                            Discover the powerful features that make ChargeAway the ultimate EV charging companion.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                        {/* Feature 1: Real-time Availability */}
                        <div className="backdrop-blur-sm bg-white/10 p-8 border border-white/10 shadow-lg">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 backdrop-blur-md bg-green-500/20 rounded-xl flex items-center justify-center border border-green-400/20">
                                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-white">Real-time Availability</h3>
                                    <p className="text-sm text-green-300">Live updates</p>
                                </div>
                            </div>
                            <p className="text-gray-200 leading-relaxed">
                                Get instant updates on charging station availability, wait times, and operational status to plan your charging stops efficiently.
                            </p>
                        </div>

                        {/* Feature 2: Payment Integration */}
                        <div className="backdrop-blur-sm bg-white/10 p-8 border border-white/10 shadow-lg">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 backdrop-blur-md bg-blue-500/20 rounded-xl flex items-center justify-center border border-blue-400/20">
                                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-white">Payment Integration</h3>
                                    <p className="text-sm text-blue-300">Seamless payments</p>
                                </div>
                            </div>
                            <p className="text-gray-200 leading-relaxed">
                                Pay for charging sessions directly through the app with multiple payment options and automatic billing for convenience.
                            </p>
                        </div>

                        {/* Feature 3: Route Planning */}
                        <div className="backdrop-blur-sm bg-white/10 p-8 border border-white/10 shadow-lg">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 backdrop-blur-md bg-purple-500/20 rounded-xl flex items-center justify-center border border-purple-400/20">
                                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4-2a1 1 0 00.553-.894V5.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-white">Route Planning</h3>
                                    <p className="text-sm text-purple-300">Smart navigation</p>
                                </div>
                            </div>
                            <p className="text-gray-200 leading-relaxed">
                                Intelligent route planning that includes charging stops based on your vehicle's range and preferred charging networks.
                            </p>
                        </div>

                        {/* Feature 4: Home Charger Hosting */}
                        <div className="backdrop-blur-sm bg-white/10 p-8 border border-white/10 shadow-lg">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 backdrop-blur-md bg-orange-500/20 rounded-xl flex items-center justify-center border border-orange-400/20">
                                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-white">Home Charger Hosting</h3>
                                    <p className="text-sm text-orange-300">Earn from charging</p>
                                </div>
                            </div>
                            <p className="text-gray-200 leading-relaxed">
                                List your home charger and earn money while helping the EV community. Set your own rates and availability.
                            </p>
                        </div>

                        {/* Feature 5: Community Reviews */}
                        <div className="backdrop-blur-sm bg-white/10 p-8 border border-white/10 shadow-lg">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 backdrop-blur-md bg-indigo-500/20 rounded-xl flex items-center justify-center border border-indigo-400/20">
                                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-white">Community Reviews</h3>
                                    <p className="text-sm text-indigo-300">User feedback</p>
                                </div>
                            </div>
                            <p className="text-gray-200 leading-relaxed">
                                Read and write reviews for charging stations and home chargers to help the community make informed decisions.
                            </p>
                        </div>

                        {/* Feature 6: Notifications */}
                        <div className="backdrop-blur-sm bg-white/10 p-8 border border-white/10 shadow-lg">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 backdrop-blur-md bg-red-500/20 rounded-xl flex items-center justify-center border border-red-400/20">
                                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM11 19H6a2 2 0 01-2-2V7a2 2 0 012-2h5m5 0v5a2 2 0 002 2h5a2 2 0 002-2V7a2 2 0 00-2-2h-5z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-white">Smart Notifications</h3>
                                    <p className="text-sm text-red-300">Stay informed</p>
                                </div>
                            </div>
                            <p className="text-gray-200 leading-relaxed">
                                Get notified about charging completion, station availability, and important updates to optimize your EV experience.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-gray-50 py-12 sm:py-16">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center rounded-xl border border-gray-200 bg-white py-10 sm:py-12 shadow-sm">
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                        Building an EV or mobility app?
                    </h3>
                    <p className="text-base sm:text-lg text-[#3c3c4a] mb-8 max-w-2xl mx-auto">
                        We have built the full stack: real-time maps, payments, route planning, and hosting networks.
                    </p>
                    <CommonButton variant="primary" size="lg" href="/contact-us">
                        Book a free consultation
                    </CommonButton>
                </div>
            </section>

            {/* Development Process Section */}
            <section className="bg-white py-12 sm:py-16 lg:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                            <span className="text-gray-200 font-normal">04.{" "}</span>Development Process
                        </h2>
                        <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
                            Our comprehensive approach to building the ChargeAway mobile application.
                        </p>
                        <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto mt-4">
                            Built with Flutter for seamless cross-platform mobile app development on iOS and Android.
                        </p>
                    </div>

                    {/* Empathy Map Grid */}
                    <div className="relative max-w-6xl mx-auto">
                        {/* Central Logo */}
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                            <Image
                                src={LogoPrecisionEngineeringDevkrest}
                                alt="Devkrest Logo"
                                width={96}
                                height={96}
                                className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 object-contain"
                            />
                        </div>

                        {/* Grid Container */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 relative">
                            {/* Top Row */}
                            <div className="space-y-0">
                                {/* Design - Top Left */}
                                <div className="border border-gray-300 p-4 sm:p-6 lg:p-8 bg-gray-50">
                                    <div className="flex items-center gap-4 mb-4 lg:mb-6">
                                        <div className="bg-yellow-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                                            Design
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:gap-6">
                                        <div className="relative">
                                            <Image
                                                src={StickyNote1}
                                                alt="User research and persona development"
                                                className="w-full h-auto"
                                            />
                                        </div>
                                        <div className="relative">
                                            <Image
                                                src={StickyNote2}
                                                alt="Wireframing and prototyping"
                                                className="w-full h-auto"
                                            />
                                        </div>
                                        <div className="relative">
                                            <Image
                                                src={StickyNote3}
                                                alt="UI/UX design and visual identity"
                                                className="w-full h-auto"
                                            />
                                        </div>
                                        <div className="relative">
                                            <Image
                                                src={StickyNote}
                                                alt="Design system and component library"
                                                className="w-full h-auto"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Development - Top Right */}
                                <div className="border border-gray-300 p-4 sm:p-6 lg:p-8 bg-gray-50">
                                    <div className="flex items-center gap-4 mb-4 lg:mb-6">
                                        <div className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                                            Development
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="relative">
                                            <Image
                                                src={DevStickyNote1}
                                                alt="Flutter app development"
                                                className="w-full h-auto"
                                            />
                                        </div>
                                        <div className="relative">
                                            <Image
                                                src={DevStickyNote2}
                                                alt="Backend API development"
                                                className="w-full h-auto"
                                            />
                                        </div>
                                        <div className="relative">
                                            <Image
                                                src={DevStickyNote3}
                                                alt="Database design"
                                                className="w-full h-auto"
                                            />
                                        </div>
                                        <div className="relative">
                                            <Image
                                                src={DevStickyNote4}
                                                alt="Third-party integrations"
                                                className="w-full h-auto"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Bottom Row */}
                            <div className="space-y-0">
                                {/* Testing - Bottom Left */}
                                <div className="border border-gray-300 p-4 sm:p-6 lg:p-8 bg-gray-50">
                                    <div className="flex items-center justify-end gap-4 mb-4 lg:mb-6">
                                        <div className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                                            Testing
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="relative">
                                            <Image
                                                src={TestStickyNote1}
                                                alt="Unit and integration testing"
                                                className="w-full h-auto"
                                            />
                                        </div>
                                        <div className="relative">
                                            <Image
                                                src={TestStickyNote2}
                                                alt="User acceptance testing"
                                                className="w-full h-auto"
                                            />
                                        </div>
                                        <div className="relative">
                                            <Image
                                                src={TestStickyNote3}
                                                alt="Performance testing"
                                                className="w-full h-auto"
                                            />
                                        </div>
                                        <div className="relative">
                                            <Image
                                                src={TestStickyNote4}
                                                alt="Beta testing"
                                                className="w-full h-auto"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Launch - Bottom Right */}
                                <div className="border border-gray-300 p-4 sm:p-6 lg:p-8 bg-gray-50">
                                    <div className="flex items-center justify-end gap-4 mb-4 lg:mb-6">
                                        <div className="bg-pink-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                                            Launch
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:gap-6">
                                        <div className="relative">
                                            <Image
                                                src={LaunchStickyNote1}
                                                alt="App store submission"
                                                className="w-full h-auto"
                                            />
                                        </div>
                                        <div className="relative">
                                            <Image
                                                src={LaunchStickyNote2}
                                                alt="Marketing and acquisition"
                                                className="w-full h-auto"
                                            />
                                        </div>
                                        <div className="relative">
                                            <Image
                                                src={LaunchStickyNote3}
                                                alt="Monitoring setup"
                                                className="w-full h-auto"
                                            />
                                        </div>
                                        <div className="relative">
                                            <Image
                                                src={LaunchStickyNote4}
                                                alt="Post-launch support"
                                                className="w-full h-auto"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <p className="text-center text-base sm:text-lg text-gray-600 max-w-3xl mx-auto mt-10 px-4">
                            We shipped{" "}
                            <Link href="/mobile-app-development-services" className="text-[#0061F1] underline underline-offset-2 hover:opacity-80">
                                Flutter app development
                            </Link>{" "}
                            for iOS and Android, plus{" "}
                            <Link href="/product-engineering" className="text-[#0061F1] underline underline-offset-2 hover:opacity-80">
                                Backend API development
                            </Link>{" "}
                            for scalable integrations.
                        </p>
                    </div>
                </div>
            </section>

            {/* Features Highlight Section */}
            <div className="py-12 sm:py-16 lg:py-20 bg-white relative overflow-hidden">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="space-y-8 lg:space-y-12">
                        {/* Section Header */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
                            <div className="space-y-4 lg:space-y-6">
                                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                                    <span className="text-gray-200 font-normal">05.{" "}</span>Features Highlight
                                </h2>
                            </div>

                            <div className="space-y-4 text-base sm:text-lg text-gray-600 leading-relaxed">
                                <p>
                                    Discover the comprehensive feature set that makes ChargeAway the ultimate EV charging companion. From real-time availability tracking to seamless payment integration, our app delivers a complete charging experience that empowers both drivers and hosts.
                                </p>
                            </div>
                        </div>

                        {/* Feature Image */}
                        <div className="w-full">
                            <Image
                                src={ChargeawayFeatureCover}
                                alt="ChargeAway App Features"
                                width={1200}
                                height={800}
                                className="w-full h-auto"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Branding Section */}
            <div className="py-12 sm:py-16 lg:py-20 bg-white">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="space-y-8 lg:space-y-12">
                        {/* Section Header */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
                            <div className="space-y-4 lg:space-y-6">
                                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                                    <span className="text-gray-200 font-normal">06.</span> Branding
                                </h2>
                            </div>

                            <div className="space-y-4 text-base sm:text-lg text-gray-600 leading-relaxed">
                                <p>
                                    Our branding approach for ChargeAway focused on creating a visual identity that conveys trust, innovation, and sustainability. We developed a comprehensive brand system that reflects the app's mission to revolutionize EV charging while maintaining accessibility and user-friendliness.
                                </p>
                            </div>
                        </div>

                        {/* Branding Section */}
                        <div className="bg-white border border-gray-300">
                            <div className="max-w-auto flex flex-col mx-auto">
                                <div className="grid grid-cols-1 lg:grid-cols-2">
                                    {/* Left Side */}
                                    <div className="lg:col-span-1 flex flex-col h-full">
                                        {/* Top Row - Poppins Font (Full Width) */}
                                        <div className="flex flex-col lg:flex-row border-r border-gray-300 py-8 lg:py-12">
                                            {/* Poppins Font */}
                                            <div className="w-full h-50 px-6 sm:px-12 lg:px-20">
                                                <div className="text-4xl sm:text-5xl lg:text-6xl text-gray-900">
                                                    <h3 className="font-bold mb-4">Poppins</h3>
                                                    <p className="text-gray-700 text-lg sm:text-xl lg:text-2xl mb-4">General Usecase Font</p>
                                                    <p className="text-gray-400 text-sm italic mb-4">Typography</p>
                                                </div>
                                            </div>
                                        </div>


                                    </div>

                                    {/* Right Side - Three Sections Layout */}
                                    <div className="lg:col-span-1 flex flex-col h-full">
                                        {/* Top Row - Problem Statement (Full Width) */}
                                        <div className="flex bg-black flex-col lg:flex-row py-8 lg:py-12">
                                            {/* Problem Statement */}
                                            <div className="w-full h-50 px-6 sm:px-12 lg:px-20">
                                                <div className="text-white">
                                                    <h3 className="text-lg sm:text-xl lg:text-2xl text-gray-100 font-light mb-2 sm:mb-4">Light</h3>
                                                    <h3 className="text-lg sm:text-xl lg:text-2xl text-gray-100 font-normal mb-2 sm:mb-4">Normal</h3>
                                                    <h3 className="text-lg sm:text-xl lg:text-2xl text-gray-100 font-bold mb-2 sm:mb-4">Bold</h3>
                                                    <h3 className="text-lg sm:text-xl lg:text-2xl text-gray-100 font-extrabold mb-2 sm:mb-4">Extra Bold</h3>
                                                </div>

                                            </div>

                                        </div>
                                    </div>
                                    <Image
                                        src="/images/case-studies/ev-charging-solution/app-logo.webp"
                                        alt="ChargeAway Hero Background"
                                        className="col-span-2"
                                        priority
                                        width={0}
                                        height={0}
                                        sizes="100vw"
                                        style={{ width: '100%', height: 'auto' }}
                                    />

                                </div>
                                <h3 className="text-xl sm:text-2xl mx-4 sm:mx-8 lg:mx-20 my-8 sm:my-12 lg:my-20 text-gray-700 mb-1">Color Palette</h3>
                                <div className="relative items-center justify-center mx-4 sm:mx-8 lg:mx-20 my-6 sm:my-8 lg:my-10 w-auto h-[160px] sm:h-[200px] lg:h-[240px] rounded-xl overflow-x-auto">
                                    {/* Card 1 */}
                                    <div className="absolute left-0 top-0 w-[220px] h-full bg-[#009900] rounded-[32px] text-white flex flex-col justify-center items-center z-40">
                                        <p className="text-lg font-medium">Primary Color</p>
                                        <p className="text-sm mt-2 opacity-80">#009900</p>
                                    </div>

                                    {/* Card 2 */}
                                    <div className="absolute left-[160px] top-0 w-[220px] h-full bg-[#0D1B3E] rounded-[32px] text-white flex flex-col justify-center items-center z-30">
                                        <p className="text-lg font-medium">Secondary</p>
                                        <p className="text-sm mt-2 opacity-80">#0D1B3E</p>
                                    </div>

                                    {/* Card 3 */}
                                    <div className="absolute left-[320px] top-0 w-[220px] h-full bg-[#34C759] rounded-[32px] text-white flex flex-col justify-center items-center z-20">
                                        <p className="text-lg font-medium">Success</p>
                                        <p className="text-sm mt-2 opacity-80">#34C759</p>
                                    </div>

                                    {/* Card 4 */}
                                    <div className="absolute left-[480px] top-0 w-[220px] h-full bg-[#FFBB34] rounded-[32px] text-black flex flex-col justify-center items-center z-10">
                                        <p className="text-lg font-medium">Warning</p>
                                        <p className="text-sm mt-2 opacity-70">#FFBB34</p>
                                    </div>

                                    {/* Card 5 */}
                                    <div className="absolute left-[640px] top-0 w-[220px] h-full bg-[#FF453A] rounded-[32px] text-white flex flex-col justify-center items-center z-0">
                                        <p className="text-lg font-medium">Error</p>
                                        <p className="text-sm mt-2 opacity-80">#FF453A</p>
                                    </div>

                                </div>
                                <Image
                                    src="/images/case-studies/ev-charging-solution/component-library.webp"
                                    alt="ChargeAway Hero Background"
                                    className="col-span-2 mt-3"
                                    priority
                                    width={0}
                                    height={0}
                                    sizes="100vw"
                                    style={{ width: '100%', height: 'auto' }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* The Result Section */}
            <div className="py-12 sm:py-16 lg:py-20 bg-[#F6F7FA] relative overflow-hidden">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="space-y-8 lg:space-y-12">
                        {/* Section Header */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
                            <div className="space-y-4 lg:space-y-6">
                                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                                    <span className="text-gray-200 font-normal">07.</span> The Result
                                </h2>
                            </div>

                            <div className="space-y-4 text-base sm:text-lg text-gray-600 leading-relaxed">
                                <p>
                                    The ChargeAway app successfully transformed the EV charging experience, delivering measurable impact across user engagement, infrastructure utilization, and community growth. Our comprehensive solution addressed critical pain points while establishing a scalable platform for sustainable transportation.
                                </p>
                            </div>
                        </div>

                        {/* Result Content */}
                        <div className="space-y-20 sm:space-y-32 lg:space-y-40">
                            {/* Splash & Onboarding */}
                            <div className="space-y-6">
                                <h3 className="text-xl sm:text-2xl font-bold text-gray-300">
                                    <span className="text-gray-300 font-normal">7.1</span>  SPLASH & ONBOARDING
                                </h3>
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <Image
                                        src="/images/case-studies/ev-charging-solution/splash/PHONE.webp"
                                        alt="Splash Screen"
                                        width={300}
                                        height={600}
                                        className="w-full sm:w-auto h-auto max-w-[200px] sm:max-w-[250px] lg:max-w-[300px]"
                                    />
                                    <Image
                                        src="/images/case-studies/ev-charging-solution/splash/PHONE-1.webp"
                                        alt="Splash Screen 1"
                                        width={300}
                                        height={600}
                                        className="w-full sm:w-auto h-auto max-w-[200px] sm:max-w-[250px] lg:max-w-[300px]"
                                    />
                                    <Image
                                        src="/images/case-studies/ev-charging-solution/splash/PHONE-2.webp"
                                        alt="Splash Screen 2"
                                        width={300}
                                        height={600}
                                        className="w-full sm:w-auto h-auto max-w-[200px] sm:max-w-[250px] lg:max-w-[300px]"
                                    />
                                    <Image
                                        src="/images/case-studies/ev-charging-solution/splash/PHONE-3.webp"
                                        alt="Splash Screen 3"
                                        width={300}
                                        height={600}
                                        className="w-full sm:w-auto h-auto max-w-[200px] sm:max-w-[250px] lg:max-w-[300px]"
                                    />

                                </div>
                            </div>
                            {/* Auth & Host Setup */}
                            <div className="space-y-12">
                                <h3 className="text-xl sm:text-2xl font-bold text-gray-300">
                                    <span className="text-gray-300 font-normal">7.2</span>  AUTH & HOST SETUP
                                </h3>
                                <div className="space-y-6 sm:space-y-8">
                                    {/* First Row - 2 screenshots */}
                                    <div className="flex flex-col sm:flex-row gap-4">
                                        <Image
                                            src="/images/case-studies/ev-charging-solution/auth/PHONE-1.webp"
                                            alt="Auth Screen 1"
                                            width={300}
                                            height={600}
                                            className="w-full sm:w-auto h-auto max-w-[200px] sm:max-w-[250px] lg:max-w-[300px]"
                                        />
                                        <Image
                                            src="/images/case-studies/ev-charging-solution/auth/PHONE-2.webp"
                                            alt="Auth Screen 2"
                                            width={300}
                                            height={600}
                                            className="w-full sm:w-auto h-auto max-w-[200px] sm:max-w-[250px] lg:max-w-[300px]"
                                        />
                                    </div>

                                    {/* Second Row - 3 screenshots */}
                                    <div className="flex flex-col sm:flex-row gap-4">
                                        <Image
                                            src="/images/case-studies/ev-charging-solution/auth/PHONE-3.webp"
                                            alt="Auth Screen 3"
                                            width={300}
                                            height={600}
                                            className="w-full sm:w-auto h-auto max-w-[200px] sm:max-w-[250px] lg:max-w-[300px]"
                                        />
                                        <Image
                                            src="/images/case-studies/ev-charging-solution/auth/PHONE-4.webp"
                                            alt="Auth Screen 4"
                                            width={300}
                                            height={600}
                                            className="w-full sm:w-auto h-auto max-w-[200px] sm:max-w-[250px] lg:max-w-[300px]"
                                        />
                                        <Image
                                            src="/images/case-studies/ev-charging-solution/auth/PHONE.webp"
                                            alt="Auth Screen 5"
                                            width={300}
                                            height={600}
                                            className="w-full sm:w-auto h-auto max-w-[200px] sm:max-w-[250px] lg:max-w-[300px]"
                                        />
                                        <Image
                                            src="/images/case-studies/ev-charging-solution/auth/HOST.webp"
                                            alt="Auth Screen 5"
                                            width={300}
                                            height={600}
                                            className="w-full sm:w-auto h-auto max-w-[200px] sm:max-w-[250px] lg:max-w-[300px]"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Booking Workflow */}
                            <div className="space-y-8 lg:space-y-12">
                                <h3 className="text-xl sm:text-2xl font-bold text-gray-300">
                                    <span className="text-gray-300 font-normal">7.3</span>  BOOKING WORKFLOW
                                </h3>
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <Image
                                        src="/images/case-studies/ev-charging-solution/home/home.webp"
                                        alt="Home Screen"
                                        width={300}
                                        height={600}
                                        className="w-full sm:w-auto h-auto max-w-[200px] sm:max-w-[250px] lg:max-w-[300px]"
                                    />
                                    <Image
                                        src="/images/case-studies/ev-charging-solution/home/details.webp"
                                        alt="Details Screen"
                                        width={300}
                                        height={600}
                                        className="w-full sm:w-auto h-auto max-w-[200px] sm:max-w-[250px] lg:max-w-[300px]"
                                    />
                                    <Image
                                        src="/images/case-studies/ev-charging-solution/home/calendar.webp"
                                        alt="Calendar Screen"
                                        width={300}
                                        height={600}
                                        className="w-full sm:w-auto h-auto max-w-[200px] sm:max-w-[250px] lg:max-w-[300px]"
                                    />
                                    <Image
                                        src="/images/case-studies/ev-charging-solution/home/payment_summary.webp"
                                        alt="Payment Summary Screen"
                                        width={300}
                                        height={600}
                                        className="w-full sm:w-auto h-auto max-w-[200px] sm:max-w-[250px] lg:max-w-[300px]"
                                    />
                                </div>
                            </div>

                            {/* Booking Flow (as User) */}
                            <div className="space-y-8 lg:space-y-12">
                                <h3 className="text-xl sm:text-2xl font-bold text-gray-300">
                                    <span className="text-gray-300 font-normal">7.4</span>  BOOKING FLOW (AS USER)
                                </h3>
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <Image
                                        src="/images/case-studies/ev-charging-solution/home/attend.webp"
                                        alt="Attend Screen"
                                        width={300}
                                        height={600}
                                        className="w-full sm:w-auto h-auto max-w-[200px] sm:max-w-[250px] lg:max-w-[300px]"
                                    />
                                    <Image
                                        src="/images/case-studies/ev-charging-solution/home/summary.webp"
                                        alt="Summary Screen"
                                        width={300}
                                        height={600}
                                        className="w-full sm:w-auto h-auto max-w-[200px] sm:max-w-[250px] lg:max-w-[300px]"
                                    />
                                    <Image
                                        src="/images/case-studies/ev-charging-solution/home/maintenance.webp"
                                        alt="Maintenance Screen"
                                        width={300}
                                        height={600}
                                        className="w-full sm:w-auto h-auto max-w-[200px] sm:max-w-[250px] lg:max-w-[300px]"
                                    />
                                </div>
                            </div>

                            {/* Chats */}
                            <div className="space-y-8 lg:space-y-12">
                                <h3 className="text-xl sm:text-2xl font-bold text-gray-300">
                                    <span className="text-gray-300 font-normal">7.5</span>  CHATS
                                </h3>
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <Image
                                        src="/images/case-studies/ev-charging-solution/chat/PHONE.webp"
                                        alt="Chat Screen 1"
                                        width={300}
                                        height={600}
                                        className="w-full sm:w-auto h-auto max-w-[200px] sm:max-w-[250px] lg:max-w-[300px]"
                                    />
                                    <Image
                                        src="/images/case-studies/ev-charging-solution/chat/PHONE-1.webp"
                                        alt="Chat Screen 2"
                                        width={300}
                                        height={600}
                                        className="w-full sm:w-auto h-auto max-w-[200px] sm:max-w-[250px] lg:max-w-[300px]"
                                    />
                                </div>
                            </div>

                            {/* Notifications & Profile */}
                            <div className="space-y-8 lg:space-y-12">
                                <h3 className="text-xl sm:text-2xl font-bold text-gray-300">
                                    <span className="text-gray-300 font-normal">7.6</span>  NOTIFICATIONS & PROFILE
                                </h3>
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <Image
                                        src="/images/case-studies/ev-charging-solution/profile/PHONE.webp"
                                        alt="Profile Screen 1"
                                        width={300}
                                        height={600}
                                        className="w-full sm:w-auto h-auto max-w-[200px] sm:max-w-[250px] lg:max-w-[300px]"
                                    />
                                    <Image
                                        src="/images/case-studies/ev-charging-solution/profile/PHONE-1.webp"
                                        alt="Profile Screen 2"
                                        width={300}
                                        height={600}
                                        className="w-full sm:w-auto h-auto max-w-[200px] sm:max-w-[250px] lg:max-w-[300px]"
                                    />
                                    <Image
                                        src="/images/case-studies/ev-charging-solution/profile/PHONE-2.webp"
                                        alt="Profile Screen 3"
                                        width={300}
                                        height={600}
                                        className="w-full sm:w-auto h-auto max-w-[200px] sm:max-w-[250px] lg:max-w-[300px]"
                                    />
                                </div>
                            </div>

                            {/* The Mobile Figma */}
                            <div className="space-y-8 lg:space-y-12">
                                <h3 className="text-xl sm:text-2xl font-bold text-gray-300">
                                    <span className="text-gray-300 font-normal">7.7</span>  THE MOBILE FIGMA
                                </h3>
                                <div className="flex justify-center">
                                    <Image
                                        src="/images/case-studies/ev-charging-solution/figma/figma-mobile.webp"
                                        alt="Mobile Figma Design"
                                        width={1200}
                                        height={900}
                                        className="w-full h-auto max-w-6xl"
                                    />
                                </div>
                            </div>

                            {/* ChargeAway Website */}
                            <div className="space-y-8 lg:space-y-12">
                                <h3 className="text-xl sm:text-2xl font-bold text-gray-300">
                                    <span className="text-gray-300 font-normal">7.8</span>  CHARGEAWAY WEBSITE
                                </h3>
                                <div className="flex justify-center">
                                    <Image
                                        src="/images/case-studies/ev-charging-solution/figma/chargeaway-website.webp"
                                        alt="ChargeAway Website Design"
                                        width={1200}
                                        height={900}
                                        className="w-full h-auto max-w-6xl"
                                    />
                                </div>
                            </div>

                            {/* ChargeAway Web App Figma */}
                            <div className="space-y-8 lg:space-y-12">
                                <h3 className="text-xl sm:text-2xl font-bold text-gray-300">
                                    <span className="text-gray-300 font-normal">7.9</span>  CHARGEAWAY WEB APP FIGMA
                                </h3>
                                <div className="flex justify-center">
                                    <Image
                                        src="/images/case-studies/ev-charging-solution/figma/chargeaway-web.webp"
                                        alt="ChargeAway Web App Figma Design"
                                        width={1200}
                                        height={900}
                                        className="w-full h-auto max-w-6xl"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Client Success Story */}
            <div className="relative py-24 overflow-hidden bg-[#292929]">
                <div className="container mx-auto px-4 max-w-6xl relative z-10">
                    {/* Title and Description - Horizontal Layout */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center mb-16">
                        <div>
                            <h2 className="text-4xl max-w-2xl md:text-5xl font-bold text-white mb-6">
                                <span className="text-gray-400 font-normal">08.{" "}</span>The 'We Want More' Moment
                            </h2>
                        </div>
                        <div>
                            <p className="text-gray-300 text-lg leading-relaxed">
                                The ultimate validation of our work comes from our clients. After delivering the ChargeAway app, our client was so impressed with the quality, communication, and results that they immediately requested to start a new mobile app project with us.
                            </p>
                        </div>
                    </div>

                    {/* Main Content - Vertical Layout */}
                    <div className="flex flex-col items-center">
                        {/* Bigger Image */}
                        <div className="mb-12 w-full max-w-5xl flex justify-center">
                            <div className="border border-white/20 rounded-lg p-4">
                                <Image
                                    src="/images/case-studies/ev-charging-solution/figma/teams-chat.webp"
                                    alt="Client Teams Chat - New Project Request"
                                    width={1000}
                                    height={800}
                                    className="w-full h-auto object-contain"
                                />
                            </div>
                        </div>

                        <div className="text-center mt-12 max-w-xl mx-auto space-y-6">
                            <p className="text-gray-300 text-lg">
                                Our work speaks for itself. Ready to be our next repeat client?
                            </p>
                            <div className="flex justify-center">
                                <CommonButton
                                    variant="outline"
                                    size="lg"
                                    href="/contact-us"
                                    className="!border-white !bg-white !text-[#000929] hover:!bg-gray-100"
                                >
                                    Start your project
                                </CommonButton>
                            </div>
                        </div>

                        <p className="text-center mt-10">
                            <Link href="/case-studies" className="text-blue-400 text-lg underline underline-offset-2 hover:text-blue-300">
                                See more case studies
                            </Link>
                        </p>

                    </div>
                </div>
            </div>

            {/* Communication: The Secret Sauce to Our Success */}
            <div className="relative py-24 overflow-hidden bg-gradient-to-b from-[#F0F8FF] to-[#E6F3FF]">
                <div className="container mx-auto px-4 max-w-6xl relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                        {/* Left column - Title, Description, and Points */}
                        <div>
                            {/* Title and Description */}
                            <div className="mb-12">
                                <h2 className="text-4xl max-w-2xl md:text-5xl font-bold text-[#2C3A59] mb-6">
                                    <span className="text-gray-200 font-normal">09.{" "}</span>Communication: The Secret Sauce to Our Success
                                </h2>
                                <p className="text-[#7C8493] max-w-2xl">
                                    What made the ChargeAway app project shine? Our relentless commitment to transparent communication that kept everyone aligned and excited throughout the journey.
                                </p>
                            </div>

                            {/* Points */}
                            <div className="space-y-8">
                                <div className="flex items-start">
                                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-green-500 flex items-center justify-center text-white mr-5">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M12 20h9"></path>
                                            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-[#2C3A59] mb-2">No Surprises, Just Progress</h3>
                                        <p className="text-gray-600">
                                            Daily updates via your preferred platform kept everyone informed, eliminating the anxiety of wondering "what's happening with my app today?"
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white mr-5">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <polyline points="12 6 12 12 16 14"></polyline>
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-[#2C3A59] mb-2">Weekly Deep Dives That Matter</h3>
                                        <p className="text-gray-600">
                                            Our focused weekly meetings weren't just check-ins—they were collaborative strategy sessions that kept your vision at the center while driving momentum.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-green-500 flex items-center justify-center text-white mr-5">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-[#2C3A59] mb-2">Problems Solved Before They Grew</h3>
                                        <p className="text-gray-600">
                                            Our team's proactive approach to challenges meant issues were identified and resolved early—keeping the project on track and your stress levels low.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white mr-5">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                            <line x1="3" y1="9" x2="21" y2="9"></line>
                                            <line x1="9" y1="21" x2="9" y2="9"></line>
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-[#2C3A59] mb-2">Full Transparency, Zero Guesswork</h3>
                                        <p className="text-gray-600">
                                            Our project tracking system gave you real-time visibility into every milestone, eliminating the black-box development experience that plagues most projects.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right side - Trello Card & Status Updates */}
                        <div className="relative space-y-2">
                            <div className="scale-75 mt-[-30px] overflow-hidden">
                                <Image
                                    src="/images/case-studies/trello-card.webp"
                                    alt="Daily Status Updates Dashboard"
                                    width={300}
                                    height={100}
                                    className="w-auto h-auto object-cover"
                                />
                            </div>
                            {/* Daily Status Updates Image */}
                            <div className="rounded-2xl scale-90 mt-4 overflow-hidden">
                                <Image
                                    src="/images/case-studies/daily-status-updates.webp"
                                    alt="Daily Status Updates Dashboard"
                                    width={600}
                                    height={400}
                                    className="w-full h-auto object-cover"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="mt-12 md:mt-0">
                <BannerSection
                    heading="Ready to Build Your Next Mobile App"
                    sub_heading="We built ChargeAway from concept to launch and the client came back for a second app. Let's build yours next."
                    button_text="Start your project"
                    button_link="/contact-us"
                />
            </div>

        </div>
    );
}
